<template>
  <div class="calendar-scroll">
    <div
      ref="header"
      class="calendar-scroll-header"
    >
      <header-close
        :back="isBookingEditMode || currentHotel ? null : back"
        :deactivate="deactivate"
      >
        {{ currentHotelName }}
      </header-close>
      <div
        role="tablist"
        aria-label="Stay dates"
        data-component="calendar-scroll-header-inputs"
        class="calendar-scroll-header-inputs"
      >
        <calendar-date-input
          v-for="dateField in dateFields"
          :key="dateField.label"
          :field="dateField.field"
          :fields="dateFields"
          :label="dateField.label"
          :input-selected="inputSelected"
          @selected="inputSelected = $event"
        />
      </div>
      <div class="calendar-scroll-header-days">
        <span
          v-for="week in [7, 1, 2, 3, 4, 5, 6]"
          :key="week"
          class="week-short"
        >
          {{ tc(`calendar.days${deviceLabel}.${week}`) }}
        </span>
      </div>
    </div>
    <calendar-month-list
      ref="calendarMonthList"
      :input-selected="inputSelected"
      :month-list-height="monthListHeight"
      :month-header-height="monthHeaderHeight"
      @month-click="monthListOpened = true"
      @day-selected="toggleInput"
      @update-month-visible="firstMonthVisible = $event"
      @months-rendered="monthsRendered = $event"
      @is-scrolling="isScrolling = $event"
      @is-auto-scrolling="isAutoScrolling = $event"
      @calculate-height="calculateHeight"
    />
    <div
      v-if="errorMessage"
      class="error-box"
      role="alert"
      :style="{ bottom: errorMessagePosition + 'px' }"
    >
      <p
        class="error-message"
        v-html="errorMessage"
      />
    </div>
    <transition name="slide-bottom">
      <calendar-footer
        v-show="showCalendarFooter"
        ref="footer"
        :class="{ compact: isCompact }"
        :run-route-validation="runRouteValidation"
        :is-compact="isCompact"
        @open-special-code="openSpecialCode"
        @currency-switcher-closed="updateFocusableElement"
      />
    </transition>
    <transition name="slide-right">
      <calendar-month-selector
        v-if="monthListOpened"
        :month-in-date="monthInDate"
        :outer-activator="$refs.monthActivator"
        :months-rendered="monthsRendered"
        @close="closeMonthSelector"
        @select-month="scrollToSelectedMonth($event)"
      />
    </transition>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import TabTrapMixin from '@mixins/tab-trap'
  import moment from 'moment'
  import {
    CalendarDateInput,
    CalendarMonthList,
    CalendarFooter,
    CalendarMonthSelector,
  } from './components'
  import HeaderClose from '../header-close/header-close.vue'

  export default {
    name: 'CalendarScroll',
    components: {
      CalendarDateInput,
      CalendarMonthList,
      CalendarFooter,
      HeaderClose,
      CalendarMonthSelector,
    },
    mixins: [TabTrapMixin],
    inject: ['localQuery'],
    props: {
      runRouteValidation: {
        type: Function,
        required: true,
      },
      deactivate: {
        type: Function,
        required: true,
      },
      currentHotel: {
        type: String,
        required: false,
      },
    },
    data: () => ({
      inputSelected: 'stayDateStart',
      monthListHeight: 0,
      monthHeaderHeight: 0,
      firstMonthVisible: '',
      monthListOpened: false,
      monthsRendered: [],
      isScrolling: false,
      errorMessagePosition: 0,
      isAutoScrolling: false,
    }),
    computed: {
      ...mapGetters('i18n', ['tc']),
      ...mapState('responsive', { checkIf: 'check', wWidth: 'width' }),
      ...mapState('currency', ['selectedCurrency']),
      ...mapState('modifyReservation', ['isBookingEditMode']),
      ...mapGetters('booking', [
        'haveClosedDaySelected',
        'havePastDaySelected',
        'haveLowerMinStaySelected',
        'monthFormat',
        'getBookingTrackingV2',
        'getQuery'
      ]),
      ...mapGetters('hotelData', ['getHotelById']),
      ...mapGetters('currency', ['orSelected']),
      selectedHotel() {
        return this.getHotelById(this.localQuery.hotelCode)
      },
      dateFields() {
        return [
          { label: 'checkIn', field: 'stayDateStart' },
          { label: 'checkOut', field: 'stayDateEnd' },
        ]
      },
      deviceLabel() {
        return this.checkIf.isMdAndDown ? 'Mobile' : 'Desktop'
      },
      errorMessage() {
        if (this.localQuery.stayDateStart && this.localQuery.stayDateEnd) {
          if (this.haveClosedDaySelected(this.localQuery)) {
            if (this.selectedHotel.datesNotAvailableMessage) {
              if (this.selectedHotel.datesNotAvailablePhone) {
                return this.generateMessageWithPhoneCTA(
                  this.selectedHotel.datesNotAvailableMessage,
                  this.selectedHotel.datesNotAvailablePhone
                )
              }
              return this.selectedHotel.datesNotAvailableMessage
            }
            return this.tc('booking.datesNotAvailable')
          }
          if (this.havePastDaySelected(this.localQuery)) {
            return this.tc('booking.datesNotAvailable')
          }
          if (this.haveLowerMinStaySelected(this.localQuery)) {
            return this.tc('booking.minDatesRequired', {
              minStayDays: this.haveLowerMinStaySelected(this.localQuery),
            })
          }
        }
        return null
      },
      currentHotelName() {
        return this.getHotelById(this.localQuery.hotelCode || this.currentHotel)
          .propertyName
      },
      monthInDate() {
        return moment(this.firstMonthVisible, this.monthFormat)
      },
      hasDatesSelected() {
        return !!this.localQuery.stayDateStart
      },
      hasSpecialCode() {
        return (
          !!this.localQuery.iataNumber
          || !!this.localQuery.promotionCode
          || !!this.localQuery.groupCode
        )
      },
      isMobile() {
        return this.wWidth <= 767
      },
      isExtraSmallScreen() {
        return this.wWidth <= 320
      },
      isCompact() {
        return !this.hasDatesSelected && !this.hasSpecialCode && this.isMobile
      },
      showCalendarFooter() {
        if (this.isMobile && !this.isExtraSmallScreen && !this.isCompact) {
          return !this.isScrolling || this.isAutoScrolling
        }
        return true
      },
    },
    watch: {
      wWidth(val) {
        if (val) {
          this.calculateHeight()
        }
      },
    },
    async mounted() {
      this.calculateHeight()
      const isChineseWebsite = window.location.href.includes('.cn')
      const chineseCurrencyCode = this.selectedHotel?.currencyCn?.isoCode
      const hotelCurrencyCode = this.selectedHotel?.currency?.isoCode

      if (
        isChineseWebsite
        && !this.selectedCurrency
        && hotelCurrencyCode
        && chineseCurrencyCode
      ) {
        await this.changeCurrency({
          toCurrency: chineseCurrencyCode,
          fromCurrency: hotelCurrencyCode,
        })
      }

      this.$track(this.getBookingTrackingV2(this.localQuery, {
        event: 'bookingStateChange',
        bookingEngine: {
          bookingStepNumber: '1.2',
          bookingStepName: 'Date Selection Flyout',
          bookingEngineVersion: '1'
        }
      }))
      
      if (window.adobe?.target?.triggerView) {
        window.adobe.target.triggerView('Date Selection Flyout')
      }
    },
    methods: {
      ...mapActions('currency', ['getRate', 'changeCurrency']),
      calculateHeight() {
        this.$nextTick(() => {
          const elHeight = this.$el.getBoundingClientRect().height
          const monthHeaderHeight = this.$refs.monthHeader
            ? this.$refs.monthHeader.getBoundingClientRect().height
            : 64
          const headerHeight = this.$refs.header.getBoundingClientRect().height
          const footerHeight = this.$refs.footer.$el.getBoundingClientRect().height
          this.errorMessagePosition = this.isCompact ? 224 : footerHeight + 16
          this.monthListHeight = elHeight - (headerHeight + footerHeight)
          this.monthHeaderHeight = monthHeaderHeight
        })
      },
      closeModal() {
        this.deactivate()
      },
      toggleInput() {
        if (this.inputSelected === 'stayDateStart') {
          this.inputSelected = 'stayDateEnd'
        } else {
          this.inputSelected = 'stayDateStart'
        }
      },
      openSpecialCode() {
        this.$emit('change-page', 'special-code')
      },
      back() {
        this.$emit('change-page', 'hotel-list')
      },
      generateMessageWithPhoneCTA(message, phone) {
        const phoneCta = `<a href="tel:${phone}">${phone}</a>`
        return message.replace('[[phone]]', phoneCta)
      },
      scrollToSelectedMonth(selectedMonth) {
        this.$refs.calendarMonthList.scrollToItem(selectedMonth)
        this.monthListOpened = false
      },
      closeMonthSelector() {
        this.monthListOpened = false
        this.updateFocusableElement()
      },
      updateFocusableElement() {
        this.firstFocusableEl.focus()
      }
    },
  }
</script>
